import React, { useState, useEffect } from 'react';
import { HelpCircle, MessageCircle, Sparkles, Calendar, FileText, Shield, Plane, DollarSign, PawPrint, AlertCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

export const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/faqs`);
        const data = await response.json();
        setFaqs(data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  // Categorize FAQs
  const categories = [
    {
      id: 'planning',
      name: 'Planning & Timeline',
      icon: Calendar,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      questions: [0] // FAQ indices that belong to this category
    },
    {
      id: 'documentation',
      name: 'Documentation & Requirements',
      icon: FileText,
      color: 'from-primary to-teal-600',
      bgColor: 'bg-primary/5',
      borderColor: 'border-primary/20',
      questions: [1, 2]
    },
    {
      id: 'costs',
      name: 'Costs & Pricing',
      icon: DollarSign,
      color: 'from-emerald-500 to-green-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      questions: [3]
    },
    {
      id: 'travel',
      name: 'Travel & Transportation',
      icon: Plane,
      color: 'from-accent to-purple-600',
      bgColor: 'bg-accent/5',
      borderColor: 'border-accent/20',
      questions: [4, 5, 7]
    },
    {
      id: 'special',
      name: 'Special Circumstances',
      icon: AlertCircle,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      questions: [8, 9]
    },
    {
      id: 'support',
      name: 'Support & Services',
      icon: Shield,
      color: 'from-indigo-500 to-blue-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      questions: [10]
    }
  ];

  // Get FAQs by category
  const getFaqsByCategory = (categoryId) => {
    if (categoryId === 'all') return faqs;
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.questions.map(idx => faqs[idx]).filter(Boolean) : [];
  };

  const filteredFaqs = selectedCategory === 'all' ? faqs : getFaqsByCategory(selectedCategory);
  
  // Get category for a specific FAQ index
  const getCategoryForFaq = (faqIndex) => {
    return categories.find(cat => cat.questions.includes(faqIndex));
  };

  if (loading) {
    return (
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">Loading FAQs...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="faq" className="py-20 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/30 rounded-full px-5 py-2.5 mb-6 shadow-sm">
            <HelpCircle className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold text-primary uppercase tracking-wide">Common Questions</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-base sm:text-lg text-foreground/70 leading-relaxed">
            Everything you need to know about relocating your pet internationally
          </p>
        </div>

        {/* Category Filter */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg scale-105'
                  : 'bg-white text-foreground/70 hover:bg-primary/10 border-2 border-primary/20'
              }`}
            >
              <PawPrint className="w-4 h-4 inline-block mr-2" />
              All Questions
            </button>
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    selectedCategory === category.id
                      ? `bg-gradient-to-r ${category.color} text-white shadow-lg scale-105`
                      : `bg-white text-foreground/70 hover:${category.bgColor} border-2 ${category.borderColor}`
                  }`}
                >
                  <IconComponent className="w-4 h-4 inline-block mr-2" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-5">
            {filteredFaqs.map((faq, index) => {
              const originalIndex = faqs.indexOf(faq);
              const category = getCategoryForFaq(originalIndex);
              const CategoryIcon = category?.icon || HelpCircle;
              
              return (
                <AccordionItem 
                  key={originalIndex} 
                  value={`item-${originalIndex}`}
                  className={`bg-white rounded-2xl border-2 ${category?.borderColor || 'border-primary/20'} shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden`}
                >
                  <AccordionTrigger className="text-left hover:no-underline px-6 py-6 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/5 transition-colors group">
                    <div className="flex items-start gap-4 pr-4 w-full">
                      <div className="flex-shrink-0 mt-1">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category?.color || 'from-primary to-accent'} flex items-center justify-center text-white shadow-lg`}>
                          <CategoryIcon className="w-6 h-6" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <span className="font-bold text-lg sm:text-xl text-foreground group-hover:text-primary transition-colors leading-relaxed">
                          {faq.question}
                        </span>
                        {selectedCategory === 'all' && (
                          <div className="mt-2">
                            <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${category?.bgColor || 'bg-primary/10'} text-foreground/70`}>
                              {category?.name || 'General'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="ml-16 pt-2">
                      <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Bottom CTA - Enhanced */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 sm:p-10 border-2 border-primary/20 shadow-xl">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent mb-4 shadow-lg">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Still Have Questions?
            </h3>
            <p className="text-base sm:text-lg text-foreground/70 mb-6 leading-relaxed">
              Our expert team is ready to answer all your pet travel questions. Schedule a consultation today!
            </p>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Schedule Your Consultation
            </button>
            <p className="text-sm text-foreground/50 mt-4">
              💚 CI$50 for up to 30 minutes
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
